import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { branchService } from '../services/branchService'
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  MapPin, 
  Phone, 
  Mail, 
  Users, 
  Clock, 
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  MoreVertical,
  Calendar,
  DollarSign,
  Car
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { cn } from '../utils/cn'

export default function BranchManagementPage() {
  const { userProfile, hasRole } = useAuth()
  const { branches, getActualBranches, switchBranch, refreshBranches } = useBranch()
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [filteredBranches, setFilteredBranches] = useState([])

  // Form state for new/edit branch
  const [branchForm, setBranchForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    managerId: '',
    isActive: true,
    settings: {
      currency: 'ZAR',
      timezone: 'Africa/Johannesburg',
      operatingHours: {
        monday: { open: '08:00', close: '17:00', isOpen: true },
        tuesday: { open: '08:00', close: '17:00', isOpen: true },
        wednesday: { open: '08:00', close: '17:00', isOpen: true },
        thursday: { open: '08:00', close: '17:00', isOpen: true },
        friday: { open: '08:00', close: '17:00', isOpen: true },
        saturday: { open: '08:00', close: '16:00', isOpen: true },
        sunday: { open: '09:00', close: '15:00', isOpen: true }
      },
      services: [
        { id: 'basic-wash', name: 'Basic Wash', price: 50, duration: 30 },
        { id: 'premium-wash', name: 'Premium Wash', price: 80, duration: 45 },
        { id: 'deluxe-wash', name: 'Deluxe Wash', price: 120, duration: 60 }
      ],
      taxRate: 15,
      loyaltyPoints: {
        earnRate: 1, // points per R1 spent
        redeemRate: 100 // R1 per 100 points
      }
    },
    metadata: {
      capacity: 10, // max cars in queue
      washBays: 3,
      staffCapacity: 5
    }
  })

  useEffect(() => {
    if (hasRole('owner')) {
      loadBranches()
    }
  }, [hasRole])

  useEffect(() => {
    filterBranches()
  }, [branches, searchTerm, statusFilter])

  const loadBranches = async () => {
    try {
      setLoading(true)
      await refreshBranches()
    } catch (error) {
      console.error('Error loading branches:', error)
      toast.error('Failed to load branches')
    } finally {
      setLoading(false)
    }
  }

  const filterBranches = () => {
    let filtered = getActualBranches()

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(branch => 
        branch.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.phone?.includes(searchTerm) ||
        branch.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(branch => branch.isActive)
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(branch => !branch.isActive)
    }

    setFilteredBranches(filtered)
  }

  const handleAddBranch = () => {
    setBranchForm({
      name: '',
      address: '',
      phone: '',
      email: '',
      managerId: '',
      isActive: true,
      settings: {
        currency: 'ZAR',
        timezone: 'Africa/Johannesburg',
        operatingHours: {
          monday: { open: '08:00', close: '17:00', isOpen: true },
          tuesday: { open: '08:00', close: '17:00', isOpen: true },
          wednesday: { open: '08:00', close: '17:00', isOpen: true },
          thursday: { open: '08:00', close: '17:00', isOpen: true },
          friday: { open: '08:00', close: '17:00', isOpen: true },
          saturday: { open: '08:00', close: '16:00', isOpen: true },
          sunday: { open: '09:00', close: '15:00', isOpen: true }
        },
        services: [
          { id: 'basic-wash', name: 'Basic Wash', price: 50, duration: 30 },
          { id: 'premium-wash', name: 'Premium Wash', price: 80, duration: 45 },
          { id: 'deluxe-wash', name: 'Deluxe Wash', price: 120, duration: 60 }
        ],
        taxRate: 15,
        loyaltyPoints: {
          earnRate: 1,
          redeemRate: 100
        }
      },
      metadata: {
        capacity: 10,
        washBays: 3,
        staffCapacity: 5
      }
    })
    setShowAddModal(true)
  }

  const handleEditBranch = (branch) => {
    setSelectedBranch(branch)
    setBranchForm({
      name: branch.name || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      managerId: branch.managerId || '',
      isActive: branch.isActive !== false,
      settings: branch.settings || {
        currency: 'ZAR',
        timezone: 'Africa/Johannesburg',
        operatingHours: {
          monday: { open: '08:00', close: '17:00', isOpen: true },
          tuesday: { open: '08:00', close: '17:00', isOpen: true },
          wednesday: { open: '08:00', close: '17:00', isOpen: true },
          thursday: { open: '08:00', close: '17:00', isOpen: true },
          friday: { open: '08:00', close: '17:00', isOpen: true },
          saturday: { open: '08:00', close: '16:00', isOpen: true },
          sunday: { open: '09:00', close: '15:00', isOpen: true }
        },
        services: [
          { id: 'basic-wash', name: 'Basic Wash', price: 50, duration: 30 },
          { id: 'premium-wash', name: 'Premium Wash', price: 80, duration: 45 },
          { id: 'deluxe-wash', name: 'Deluxe Wash', price: 120, duration: 60 }
        ],
        taxRate: 15,
        loyaltyPoints: {
          earnRate: 1,
          redeemRate: 100
        }
      },
      metadata: branch.metadata || {
        capacity: 10,
        washBays: 3,
        staffCapacity: 5
      }
    })
    setShowEditModal(true)
  }

  const handleViewDetails = (branch) => {
    setSelectedBranch(branch)
    setShowDetailsModal(true)
  }

  const handleDeleteBranch = async (branch) => {
    if (!confirm(`Are you sure you want to delete "${branch.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      await branchService.deleteBranch(branch.id)
      toast.success(`Branch "${branch.name}" deleted successfully`)
      await loadBranches()
    } catch (error) {
      console.error('Error deleting branch:', error)
      toast.error('Failed to delete branch')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!branchForm.name || !branchForm.address) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      if (showEditModal && selectedBranch) {
        await branchService.updateBranch(selectedBranch.id, branchForm)
        toast.success(`Branch "${branchForm.name}" updated successfully`)
      } else {
        await branchService.createBranch(branchForm)
        toast.success(`Branch "${branchForm.name}" created successfully`)
      }
      
      setShowAddModal(false)
      setShowEditModal(false)
      await loadBranches()
    } catch (error) {
      console.error('Error saving branch:', error)
      toast.error('Failed to save branch')
    }
  }

  const handleFormChange = (field, value) => {
    setBranchForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSettingsChange = (field, value) => {
    setBranchForm(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }))
  }

  if (!hasRole('owner')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to manage branches.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <Building2 className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Branch Management
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Manage your business branches and locations
          </p>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={handleAddBranch}
            className="rounded-xl bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Plus className="h-5 w-5 inline mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Add New Branch
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search branches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Branches Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBranches.map((branch, index) => (
            <div 
              key={branch.id}
              className="group relative rounded-xl border border-border/80 bg-card shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Status indicator */}
              <div className={cn(
                'absolute top-4 right-4 w-3 h-3 rounded-full border-2 border-white shadow-md',
                branch.isActive ? 'bg-green-400' : 'bg-red-400'
              )} />
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-3 text-primary">
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{branch.name}</h3>
                      <p className="text-sm text-gray-600">{branch.address}</p>
                    </div>
                  </div>
                </div>

                {/* Branch Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{branch.address}</span>
                  </div>
                  {branch.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{branch.phone}</span>
                    </div>
                  )}
                  {branch.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{branch.email}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-primary/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-blue-600">{branch.stats?.totalWashes || 0}</div>
                    <div className="text-xs text-gray-600">Total Washes</div>
                  </div>
                  <div className="bg-emerald-500/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-green-600">R{branch.stats?.totalRevenue || 0}</div>
                    <div className="text-xs text-gray-600">Revenue</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(branch)}
                      className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-xl transition-all duration-200"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditBranch(branch)}
                      className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-xl transition-all duration-200"
                      title="Edit Branch"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBranch(branch)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                      title="Delete Branch"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                    branch.isActive 
                      ? "bg-green-100 text-green-800" 
                      : "bg-red-100 text-red-800"
                  )}>
                    {branch.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredBranches.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No branches found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first branch.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={handleAddBranch}
                className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
              >
                <Plus className="h-5 w-5 inline mr-2" />
                Add First Branch
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Branch Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                  <Building2 className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {showEditModal ? 'Edit Branch' : 'Add New Branch'}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Branch Name *
                    </label>
                    <input
                      type="text"
                      value={branchForm.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={branchForm.isActive ? 'active' : 'inactive'}
                      onChange={(e) => handleFormChange('isActive', e.target.value === 'active')}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    value={branchForm.address}
                    onChange={(e) => handleFormChange('address', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    required
                  />
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={branchForm.phone || ''}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={branchForm.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>

                {/* Settings */}
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Branch Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Currency
                      </label>
                      <select
                        value={branchForm.settings?.currency || 'ZAR'}
                        onChange={(e) => handleSettingsChange('currency', e.target.value)}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      >
                        <option value="ZAR">South African Rand (R)</option>
                        <option value="USD">US Dollar ($)</option>
                        <option value="EUR">Euro (€)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Time Zone
                      </label>
                      <select
                        value={branchForm.settings?.timezone || 'Africa/Johannesburg'}
                        onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      >
                        <option value="Africa/Johannesburg">South Africa (SAST)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    {showEditModal ? 'Update Branch' : 'Create Branch'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 